import { useCallback, useEffect, useRef, useState } from 'react';
import { getOasisExportReadiness } from '../services/api';
import type { OasisExportReadiness } from '../services/api/types.gen';

export type OasisExportReadinessById = Record<string, OasisExportReadiness>;

// Each check scrubs every chart on the page server-side, so switching windows
// back and forth must not re-run it every time.
const FOCUS_REFRESH_MIN_MS = 30_000;

/**
 * Which of these Completed OASIS charts CMS will reject, and why.
 *
 * Asked after the list has loaded so a slow scrub never holds the list up, and
 * again when the window regains focus, since the usual fix happens in another
 * tab. A failed request keeps the last answer rather than unflagging every row;
 * the export endpoints enforce the same check either way.
 */
export function useOasisExportReadiness(visitIds: string[]): {
  readiness: OasisExportReadinessById;
  /** True while the latest check is still out. */
  checking: boolean;
  refresh: () => Promise<void>;
} {
  const [readiness, setReadiness] = useState<OasisExportReadinessById>({});
  // Joined so a re-render that rebuilds the same id list does not refetch.
  const key = visitIds.join(',');
  const [checking, setChecking] = useState(false);
  const latestRequest = useRef(0);
  const lastCheckedAt = useRef(0);

  const refresh = useCallback(async () => {
    const ids = key ? key.split(',') : [];
    const request = ++latestRequest.current;
    lastCheckedAt.current = Date.now();
    if (ids.length === 0) {
      setReadiness({});
      setChecking(false);
      return;
    }
    setChecking(true);
    try {
      const response = await getOasisExportReadiness({
        body: { visit_ids: ids },
      });
      // A page change can start a newer request before this one returns.
      if (request !== latestRequest.current) return;
      if (response.error) {
        console.error(
          'Failed to check OASIS export readiness:',
          response.error
        );
        return;
      }
      setReadiness(response.data?.results ?? {});
    } catch (err) {
      if (request !== latestRequest.current) return;
      console.error('Failed to check OASIS export readiness:', err);
    } finally {
      if (request === latestRequest.current) setChecking(false);
    }
  }, [key]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const trailingRefresh = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const onFocus = () => {
      const wait = FOCUS_REFRESH_MIN_MS - (Date.now() - lastCheckedAt.current);
      if (wait <= 0) {
        void refresh();
        return;
      }
      // Checked moments ago: check again once the window has passed, so a
      // chart fixed in another tab still clears without leaving the page.
      if (trailingRefresh.current) return;
      trailingRefresh.current = setTimeout(() => {
        trailingRefresh.current = null;
        void refresh();
      }, wait);
    };
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      if (trailingRefresh.current) clearTimeout(trailingRefresh.current);
      trailingRefresh.current = null;
    };
  }, [refresh]);

  return { readiness, checking, refresh };
}
